import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { useEventClock, findLive, findNext } from "@/lib/scheduleNow";
import { WinnersBanner } from "@/components/WinnersBanner";
import { CalendarDays, MapPin, Ticket, Megaphone, Gavel, Map, ArrowRight, Medal, Heart } from "lucide-react";

const EVENT_DATE = new Date("2026-10-30T08:00:00-04:00");

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff === 0 };
}

const QUICK = [
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/mustangs", label: "Meet the Mustangs", icon: Medal },
  { to: "/scores", label: "Live Scores", icon: Medal },
  { to: "/fan-favorite", label: "Fan Favorite", icon: Heart },
  { to: "/mustangs?auction=1", label: "Auction", icon: Gavel },
  { to: "/more#map", label: "Event Map", icon: Map },
];

export default function Home() {
  const cd = useCountdown(EVENT_DATE);
  const [announcements, setAnnouncements] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/schedule").then((r) => setSchedule(r.data)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data.filter((s) => s.featured))).catch(() => {});
  }, []);

  const clock = useEventClock();
  const happeningNow = findLive(schedule, clock);
  const upcoming = findNext(schedule, clock) || schedule.find((s) => s.status === "Upcoming");
  const firstEvent = schedule[0];
  const eventStarted = cd.done;

  const fmtDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString("en-US", { month: "long", day: "numeric" });
  };

  return (
    <AppLayout>
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1777588038984-58619e25dbc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-primary/85" />
        </div>
        <div className="relative px-5 pt-10 pb-8 text-white">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Official Event Guide</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl">
            Bluegrass<br />Mustang Makeover
          </h1>
          <div className="mt-4 space-y-1.5 text-sm">
            <p className="flex items-center gap-2"><CalendarDays size={16} className="text-secondary" /> October 30–31, 2026</p>
            <p className="flex items-center gap-2"><MapPin size={16} className="text-secondary" /> Henry County Fairgrounds, Eminence, KY</p>
          </div>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Ticket size={14} /> Free Admission
          </span>

          {/* Countdown */}
          {!cd.done ? (
            <div data-testid="countdown" className="mt-6 grid grid-cols-4 gap-2">
              {[["Days", cd.d], ["Hrs", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-white/10 backdrop-blur py-3 text-center">
                  <div className="font-display text-2xl font-bold tabular-nums">{String(v).padStart(2, "0")}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{l}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-xl bg-secondary px-4 py-3 font-bold">The event is live! 🐴</div>
          )}
        </div>
      </div>

      <WinnersBanner />

      {/* Happening now / upcoming */}
      <div className="px-4 pt-6">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          {!eventStarted && firstEvent ? (
            <>
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-secondary">First Event</p>
              <h3 className="font-display text-xl font-bold text-primary">{firstEvent.name}</h3>
              <p className="text-sm text-muted-foreground">{fmtDate(firstEvent.date)} at {firstEvent.start_time}{firstEvent.arena && firstEvent.arena !== "TBD" ? ` · ${firstEvent.arena}` : ""}</p>
            </>
          ) : happeningNow ? (
            <Link to="/schedule" data-testid="home-now-happening" className="block">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-widest text-secondary">Happening Now</p>
                <StatusBadge status="Happening Now" />
              </div>
              <h3 className="font-display text-xl font-bold text-primary">{happeningNow.name}</h3>
              <p className="text-sm text-muted-foreground">{happeningNow.start_time} · {happeningNow.arena}</p>
              {upcoming && <p className="mt-2 text-xs text-muted-foreground"><span className="font-bold text-secondary">Up next:</span> {upcoming.start_time} · {upcoming.name}</p>}
            </Link>
          ) : upcoming ? (
            <>
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-secondary">Up Next</p>
              <h3 className="font-display text-xl font-bold text-primary">{upcoming.name}</h3>
              <p className="text-sm text-muted-foreground">{upcoming.start_time} · {upcoming.arena}</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Full schedule coming soon.</p>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="px-4 pt-6">
        <h2 className="mb-3 font-display text-xl font-bold text-primary">Quick Access</h2>
        <div className="grid grid-cols-2 gap-3">
          {QUICK.map(({ to, label, icon: Icon }, i) => (
            <Link key={label} to={to} data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md ${i === 0 ? "col-span-2" : ""}`}>
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Icon size={20} /></span>
              <span className="font-semibold text-primary">{label}</span>
              <ArrowRight size={16} className="ml-auto text-muted-foreground" />
            </Link>
          ))}
        </div>
      </div>

      {/* Announcements */}
      <div className="px-4 pt-6">
        <div className="mb-3 flex items-center gap-2">
          <Megaphone size={18} className="text-secondary" />
          <h2 className="font-display text-xl font-bold text-primary">Latest Announcements</h2>
        </div>
        <div className="space-y-2">
          {announcements.length === 0 && <p className="text-sm text-muted-foreground">No announcements yet.</p>}
          {announcements.slice(0, 4).map((a) => (
            <div key={a.id} data-testid={`announcement-${a.id}`} className="rounded-xl border border-border bg-card p-3 shadow-sm">
              <p className="font-semibold text-primary">{a.title}</p>
              <p className="text-sm text-muted-foreground">{a.message}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <div className="px-4 pt-6">
          <h2 className="mb-3 font-display text-xl font-bold text-primary">Featured Sponsors</h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {sponsors.map((s) => (
              <div key={s.id} className="min-w-[140px] rounded-xl border border-border bg-card p-4 text-center shadow-sm">
                <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-secondary/15 font-display text-lg font-bold text-secondary">
                  {s.name.charAt(0)}
                </div>
                <p className="text-sm font-semibold text-primary">{s.name}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-secondary">{s.level}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
